// Enhanced content extraction function for templates
export const extractTemplateContent = (content: any): string => {
  console.log('🔍 Extracting content from:', content);
  console.log('🏷️ Content type:', typeof content);
  
  if (!content) {
    console.log('❌ No content provided');
    return '';
  }

  // If it's already a string
  if (typeof content === 'string') {
    console.log('✅ Content is string, length:', content.length);
    
    // Try to parse as JSON first
    try {
      const parsed = JSON.parse(content);
      console.log('🔄 Successfully parsed string as JSON:', parsed);
      // Recursively process the parsed object
      return extractTemplateContent(parsed);
    } catch (e) {
      console.log('ℹ️ String is not JSON, treating as direct content');
      return content;
    }
  }

  // If it's an object, try different extraction methods
  if (typeof content === 'object') {
    console.log('📦 Content is object, keys:', Object.keys(content));
    
    // Try direct text property - check if property exists, not just truthiness
    if (Object.prototype.hasOwnProperty.call(content, 'text')) {
      const textValue = content.text;
      console.log('✅ Found content.text property, value:', textValue, 'type:', typeof textValue);
      // Return the text value even if it's an empty string, null, or undefined
      return textValue || '';
    }
    
    // Legacy check for truthiness (for backwards compatibility)
    if (content.text) {
      console.log('✅ Found content.text, length:', content.text.length);
      return content.text;
    }
    
    // Try other common properties
    const possibleKeys = ['content', 'body', 'data', 'value'];
    for (const key of possibleKeys) {
      if (Object.prototype.hasOwnProperty.call(content, key)) {
        const value = content[key];
        console.log(`✅ Found content.${key}, value:`, value, 'type:', typeof value);
        if (typeof value === 'string') {
          return value;
        }
      }
      // Legacy check for backwards compatibility
      if (content[key] && typeof content[key] === 'string') {
        console.log(`✅ Found content.${key}, length:`, content[key].length);
        return content[key];
      }
    }
    
    // Try to parse as JSON if it's a string representation
    try {
      const stringified = JSON.stringify(content);
      console.log('🔄 Trying to parse JSON string...');
      const parsed = JSON.parse(stringified);
      if (Object.prototype.hasOwnProperty.call(parsed, 'text')) {
        console.log('✅ Found text in parsed JSON, value:', parsed.text);
        return parsed.text || '';
      }
      // Legacy check
      if (parsed.text) {
        console.log('✅ Found text in parsed JSON, length:', parsed.text.length);
        return parsed.text;
      }
    } catch (e) {
      console.log('❌ Failed to parse as JSON:', e);
    }
    
    // Deep search for any text property
    const deepSearch = (obj: any): string => {
      if (typeof obj === 'string') return obj;
      if (typeof obj !== 'object' || obj === null) return '';
      
      for (const [key, value] of Object.entries(obj)) {
        if (key === 'text') {
          console.log('✅ Found text in deep search, value:', value, 'type:', typeof value);
          return typeof value === 'string' ? value : String(value || '');
        }
        // Legacy check
        if (key === 'text' && typeof value === 'string') {
          console.log('✅ Found text in deep search, length:', value.length);
          return value;
        }
        if (typeof value === 'object') {
          const result = deepSearch(value);
          if (result) return result;
        }
      }
      return '';
    };
    
    const deepResult = deepSearch(content);
    if (deepResult !== '') {
      return deepResult;
    }
    
    // Try regex extraction as last resort
    try {
      const stringified = JSON.stringify(content);
      console.log('🔄 Trying regex extraction on stringified content...');
      // Updated regex to capture empty strings and null values
      const textMatch = stringified.match(/"text"\s*:\s*"([^"]*)"/);
      if (textMatch) {
        const extractedText = textMatch[1].replace(/\\n/g, '\n').replace(/\\"/g, '"');
        console.log('✅ Found text via regex, length:', extractedText.length);
        return extractedText;
      }
      
      // Also try to match null values
      const nullMatch = stringified.match(/"text"\s*:\s*null/);
      if (nullMatch) {
        console.log('✅ Found null text via regex');
        return '';
      }
    } catch (e) {
      console.log('❌ Regex extraction failed:', e);
    }
  }
  
  console.log('❌ No content could be extracted');
  return '';
};